import MasmedHero from "./components/MasmedHero";
import { useVisionMision } from "@/hooks/useVisionMision";
import { useObjetivos } from "@/hooks/useObjetivos";
import { useHero } from "@/hooks/useHero";
import { heroContent } from "@/data/heroContent";
import { useEtapasEstudio } from "@/hooks/useEtapasEstudio";
import { useEquipo } from "@/hooks/useEquipo";
import TeamAvatar from "./components/TeamAvatar";
import { useEffect } from "react";

const heroFallback = heroContent.find((h) => h.page === "masmed")!;

const fallbackVisionMision = [
  {
    id: 1,
    titulo: "Visión",
    descripcion: "Ser un proyecto referente en el estudio y comprensión de las necesidades de salud y especialistas médicos en regiones, aportando conocimiento útil para construir una salud más cercana, equitativa y pensada desde el territorio.",
    icono: "ri-eye-line",
  },
  {
    id: 2,
    titulo: "Misión",
    descripcion: "Generar y difundir información basada en evidencia sobre las brechas y necesidades del capital humano médico en Tarapacá, promoviendo la articulación territorial y contribuyendo al fortalecimiento de la salud regional.",
    icono: "ri-focus-3-line",
  },
];

const fallbackObjetivos = [
  { id: 1, orden: "01", titulo: "Lorem ipsum dolor sit amet, consectetuer", descripcion: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh Lorem ipsum dolor sit amet, consectetuer." },
  { id: 2, orden: "02", titulo: "Lorem ipsum dolor sit amet, consectetuer", descripcion: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh Lorem ipsum dolor sit amet, consectetuer." },
  { id: 3, orden: "03", titulo: "Lorem ipsum dolor sit amet, consectetuer", descripcion: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh Lorem ipsum dolor sit amet, consectetuer." },
  { id: 4, orden: "04", titulo: "Lorem ipsum dolor sit amet, consectetuer", descripcion: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh Lorem ipsum dolor sit amet, consectetuer." },
  { id: 5, orden: "05", titulo: "Lorem ipsum dolor sit amet, consectetuer", descripcion: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh Lorem ipsum dolor sit amet, consectetuer." },
];

export default function Masmed() {
  const { data: dbVisionMision } = useVisionMision();
  const { data: dbObjetivos } = useObjetivos();
  const { data: equipo, loading: equipoLoading, error: equipoError } = useEquipo();
  const { data: masmedHero } = useHero("masmed", heroFallback);
  const { data: etapas } = useEtapasEstudio();

  // Update page title from hero seo_title
  useEffect(() => {
    const seoTitle = masmedHero?.seo_title ?? heroFallback.seoTitle ?? "MásMed Tarapacá";
    if (seoTitle) {
      document.title = seoTitle;
    }
  }, [masmedHero]);

  const visionMision = dbVisionMision.length > 0 ? dbVisionMision : fallbackVisionMision;
  const objetivos = dbObjetivos.length > 0 ? dbObjetivos : fallbackObjetivos;

  return (
    <div>
      <MasmedHero />

      {/* El Proyecto */}
      <section className="py-10 md:py-12 lg:py-14 bg-white overflow-x-hidden">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-10 items-stretch">
            {/* Columna izquierda */}
            <div className="lg:w-7/12 flex flex-col h-full overflow-hidden">
              <h2 className="text-masmed-gray-800 text-2xl md:text-[1.75rem] mb-3">
                El proyecto
              </h2>
              {/* Onda decorativa */}
              <svg className="w-48 h-5 text-primary mb-5 shrink-0" viewBox="0 0 240 12" fill="none">
                <path
                  d="M0 6 C10 0, 20 12, 30 6 C40 0, 50 12, 60 6 C70 0, 80 12, 90 6 C100 0, 110 12, 120 6 C130 0, 140 12, 150 6 C160 0, 170 12, 180 6 C190 0, 200 12, 210 6 C220 0, 230 12, 240 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <div className="text-masmed-gray-600 text-sm leading-[1.8] text-justify space-y-4 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
                <p>
                  MásMed Tarapacá es una iniciativa regional enfocada en el estudio de las brechas de especialistas médicos y las necesidades de salud existentes en la Región de Tarapacá. El proyecto nace frente a la necesidad de comprender cómo factores como las distancias, la ruralidad, las diferencias territoriales y el acceso desigual a la atención médica impactan directamente la calidad de vida de las personas y el desarrollo sanitario de la región.
                </p>
                <p>
                  En un territorio diverso como Tarapacá, donde las realidades de comunas urbanas y rurales son distintas, MásMed busca generar información clara, territorial y basada en evidencia que permita comprender mejor los desafíos que enfrenta actualmente la salud regional.
                </p>
                <p>
                  Para ello, el proyecto levanta y analiza información relacionada con el capital humano médico, las brechas de especialistas y las necesidades presentes en distintos territorios de la región, promoviendo además el diálogo y la articulación entre instituciones públicas, profesionales de la salud, academia, actores territoriales y comunidad.
                </p>
                <p>
                  MásMed Tarapacá trabaja desde una mirada colaborativa y territorial, desarrollando estudios, espacios de vinculación, transferencia de conocimiento y actividades de difusión orientadas a fortalecer la comprensión de la salud regional y aportar insumos para futuras decisiones en salud pública.
                </p>
                <p>
                  La iniciativa busca contribuir al fortalecimiento del desarrollo sanitario de Tarapacá, promoviendo una visión más cercana, equitativa y conectada con las necesidades reales de las personas y los territorios.
                </p>
              </div>
            </div>

            {/* Columna derecha: Visión y Misión */}
            <div className="lg:w-5/12 flex flex-col h-full gap-7 md:gap-8">
              {visionMision.map((item) => (
                <div key={item.id} className="flex gap-4">
                  <div className="w-1.5 bg-primary rounded-full shrink-0 self-stretch" />
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <h3 className="text-masmed-gray-900 text-xl md:text-[1.65rem]" style={{ fontFamily: "var(--font-heading)" }}>
                        {item.titulo}
                      </h3>
                      <i className="ri-check-line text-primary text-[1.35rem]" />
                    </div>
                    <p className="text-masmed-gray-700 text-sm leading-[1.75] text-justify italic">
                      &ldquo;{item.descripcion}&rdquo;
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Objetivos */}
      <section className="relative bg-white">
        {/* Imagen de fondo panorámica */}
        <div className="relative w-full h-[300px] md:h-[360px] overflow-hidden">
          <img
            src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/c6189a45fc5c5c31abd02f20d3644687.png"
            alt="Paisaje Tarapacá"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/50" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <h2 className="text-white text-3xl md:text-4xl lg:text-[2.5rem] tracking-wide"
              style={{ fontFamily: "var(--font-heading)" }}>
              Objetivos
            </h2>
            <div className="w-20 h-1 bg-white/60 rounded-full mt-4" />
          </div>
        </div>

        {/* Tarjetas */}
        <div className="relative z-10 -mt-20 md:-mt-24 max-w-[1180px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-wrap justify-center gap-5 md:gap-6">
            {objetivos.map((obj, index) => {
              const isEven = index % 2 === 1;
              return (
                <div
                  key={obj.id}
                  className="w-full md:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] bg-white rounded-xl p-6 md:p-8 flex flex-col group hover:-translate-y-1 transition-all duration-300 cursor-default"
                  style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}
                >
                  {/* Header con número y línea */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-sm shrink-0 ${isEven ? "bg-primary text-white" : "bg-background-100 text-foreground-900 border border-background-200"}`}
                      style={{ fontFamily: "var(--font-heading)", fontWeight: 700 }}
                    >
                      {obj.orden}
                    </div>
                    <div className="flex-1 h-px bg-background-200" />
                  </div>

                  {/* Título */}
                  <h4 className="text-foreground-900 text-sm md:text-base font-bold mb-3 leading-snug break-words"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    {obj.titulo}
                  </h4>

                  {/* Descripción */}
                  <p className="text-foreground-600 text-xs md:text-sm leading-[1.7] flex-1 break-words">
                    {obj.descripcion}
                  </p>

                  {/* Línea decorativa ondulada */}
                  <svg className="w-16 h-4 text-primary mt-5 self-start" viewBox="0 0 64 12" fill="none">
                    <path
                      d="M0 6 C8 0, 16 12, 24 6 C32 0, 40 12, 48 6 C56 0, 64 12, 64 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              );
            })}
          </div>
        </div>

        <div className="h-16 md:h-20" />
      </section>

      {/* Equipo */}
      <section className="py-12 md:py-14 lg:py-16 bg-white">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 lg:px-12">
          {/* Título centrado sin línea naranja */}
          <h2 className="text-center text-masmed-gray-800 text-2xl md:text-[1.75rem] mb-10 md:mb-14">
            El equipo
          </h2>

          {/* Estado de carga */}
          {equipoLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Estado de error */}
          {equipoError && !equipoLoading && (
            <div className="text-center py-8">
              <p className="text-red-600 text-sm">{equipoError}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-primary text-sm underline cursor-pointer"
              >
                Reintentar
              </button>
            </div>
          )}

          {/* Sin datos */}
          {!equipoLoading && !equipoError && equipo.length === 0 && (
            <p className="text-center text-masmed-gray-500 text-sm py-8">
              No hay integrantes registrados.
            </p>
          )}

          {/* Lista de integrantes */}
          {!equipoLoading && !equipoError && equipo.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 md:gap-y-10">
              {equipo.map((member, index) => {
                const isLast = index === equipo.length - 1;
                return (
                  <div
                    key={member.id}
                    className={`flex items-center gap-3 md:gap-4 ${!isLast ? "border-b border-masmed-gray-200 pb-8 md:pb-10" : ""}`}
                  >
                    {/* Línea vertical naranja proporcional al avatar */}
                    <div className="w-1 md:w-1.5 h-16 md:h-20 bg-primary rounded-full shrink-0 self-center" />

                    {/* Avatar */}
                    <TeamAvatar foto={member.foto} nombre={member.nombre} />

                    {/* Datos del integrante */}
                    <div className="flex flex-col justify-center min-w-0">
                      <h4
                        className="text-masmed-gray-900 text-sm md:text-[0.95rem] break-words leading-snug"
                        style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                      >
                        {member.nombre}
                      </h4>
                      <p className="text-masmed-gray-600 text-xs md:text-sm mt-0.5 break-words leading-snug">
                        {member.cargo}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Etapas del Estudio */}
      <section className="relative w-full overflow-hidden">
        {/* Imagen de fondo panorámica */}
        <img
          src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/f1f7e4347d73979b0980e320f0c44efe.jpeg"
          alt="Paisaje Tarapacá"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />

        {/* Contenido */}
        <div className="relative z-10 flex flex-col items-center px-4 md:px-8 lg:px-12 py-14 md:py-18 lg:py-20">
          {/* Header con título */}
          <div className="text-center mb-10 md:mb-14">
            <h2
              className="text-white text-3xl md:text-4xl lg:text-[2.5rem] tracking-wide"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Etapas del Estudio
            </h2>
            <div className="w-20 h-1 bg-white/60 rounded-full mt-4 mx-auto" />
          </div>

          {/* Tarjetas */}
          <div className="w-full max-w-[1180px] mx-auto flex flex-wrap justify-center gap-5 md:gap-6">
            {etapas.map((etapa, index) => {
              const isEven = index % 2 === 1;
              return (
                <div
                  key={etapa.id}
                  className="w-full md:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1.125rem)] rounded-xl p-6 md:p-7 flex flex-col group hover:-translate-y-1 transition-all duration-300 cursor-default"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                  }}
                >
                  {/* Header con número y línea */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm shrink-0 ${isEven ? "bg-primary text-white" : "bg-background-100 text-foreground-900 border border-background-200"}`}
                      style={{ fontFamily: "var(--font-heading)", fontWeight: 700 }}
                    >
                      {String(etapa.orden || index + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1 h-px bg-background-200" />
                  </div>

                  {/* Título */}
                  <h3
                    className="text-foreground-900 text-sm md:text-base font-bold mb-2 leading-snug break-words"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {etapa.titulo}
                  </h3>

                  {/* Línea decorativa con color de la etapa */}
                  <div
                    className="w-12 h-0.5 rounded-full mb-3"
                    style={{ backgroundColor: etapa.color || "#E45B2D" }}
                  />

                  {/* Descripción */}
                  <p className="text-foreground-700 text-sm md:text-[0.95rem] leading-[1.75] break-words flex-1">
                    {etapa.descripcion}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}